import { createTheme, responsiveFontSizes, PaletteMode } from "@mui/material/styles";
import { logoNoBackground, authBackground } from "assets";

declare module "@mui/material/styles" {
  interface Theme {
    gradients: {
      primary: string;
      secondary: string;
      dark: string;
      darkCard: string;
      referralCard: string;
      accent: string;
      success: string;
      warning: string;
      error: string;
    };
    customComponents: {
      card: {
        white: object;
        dark: object;
        static: object;
        referral: object;
        darkSection: object;
        glass: object;
        elevated: object;
      };
      container: {
        main: object;
        dark: object;
        gradient: object;
      };
    };
  }
  interface ThemeOptions {
    gradients?: {
      primary?: string;
      secondary?: string;
      dark?: string;
      darkCard?: string;
      referralCard?: string;
      accent?: string;
      success?: string;
      warning?: string;
      error?: string;
    };
    customComponents?: {
      card?: {
        white?: object;
        dark?: object;
        static?: object;
        referral?: object;
        darkSection?: object;
        glass?: object;
        elevated?: object;
      };
      container?: {
        main?: object;
        dark?: object;
        gradient?: object;
      };
    };
  }
}

export const createAppTheme = (mode: PaletteMode) => responsiveFontSizes(
  createTheme({
    palette: {
      mode,
      primary: {
        main: mode === "light" ? "#1a1a1a" : "#ffffff",
        light: mode === "light" ? "#4a5568" : "#f5f5f5",
        dark: mode === "light" ? "#000000" : "#e0e0e0",
        contrastText: mode === "light" ? "#ffffff" : "#1a1a1a",
      },
      secondary: {
        main: mode === "light" ? "#f8fafe" : "#2d2d2d",
        light: mode === "light" ? "#ffffff" : "#404040",
        dark: mode === "light" ? "#e2e8f0" : "#1a1a1a",
        contrastText: mode === "light" ? "#1a1a1a" : "#ffffff",
      },
      background: {
        default: mode === "light" ? "#fafafa" : "#121212",
        paper: mode === "light" ? "#ffffff" : "#1e1e1e",
      },
      text: {
        primary: mode === "light" ? "rgba(0, 0, 0, 0.87)" : "rgba(255, 255, 255, 0.87)",
        secondary: mode === "light" ? "rgba(0, 0, 0, 0.6)" : "rgba(255, 255, 255, 0.6)",
        disabled: mode === "light" ? "rgba(0, 0, 0, 0.38)" : "rgba(255, 255, 255, 0.38)",
      },
      divider: mode === "light" ? "rgba(0, 0, 0, 0.08)" : "rgba(255, 255, 255, 0.08)",
      error: {
        main: "#f44336",
        light: "#ef5350",
        dark: "#d32f2f",
      },
      warning: {
        main: "#ff9800",
        light: "#ffb74d",
        dark: "#f57c00",
      },
      info: {
        main: "#2196f3",
        light: "#64b5f6",
        dark: "#1976d2",
      },
      success: {
        main: "#4caf50",
        light: "#81c784",
        dark: "#388e3c",
      },
      grey: {
        50: "#fafafa",
        100: "#f5f5f5",
        200: "#eeeeee",
        300: "#e0e0e0",
        400: "#bdbdbd",
        500: "#9e9e9e",
        600: "#757575",
        700: "#616161",
        800: "#424242",
        900: "#212121",
        A100: "#f5f5f5",
        A200: "#eeeeee",
        A400: "#bdbdbd",
        A700: "#616161",
      },
    },
    spacing: 8,
    shape: {
      borderRadius: 8,
    },
    gradients: {
      primary: mode === "light" 
        ? "linear-gradient(135deg, #f8fafe 0%, #ffffff 100%)"
        : "linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)",
      secondary: mode === "light"
        ? "linear-gradient(135deg, #ffffff 0%, #f0f7ff 100%)"
        : "linear-gradient(135deg, #404040 0%, #2d2d2d 100%)",
      dark: "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)",
      darkCard: "linear-gradient(135deg, #2a2a2a 0%, #000000 100%)",
      referralCard: mode === "light"
        ? "linear-gradient(135deg, rgba(248, 250, 254, 0.8) 0%, rgba(255, 255, 255, 0.95) 100%)"
        : "linear-gradient(135deg, rgba(45, 45, 45, 0.8) 0%, rgba(30, 30, 30, 0.95) 100%)",
      accent: mode === "light"
        ? "linear-gradient(135deg, #f0f7ff 0%, #ffffff 100%)"
        : "linear-gradient(135deg, #404040 0%, #2d2d2d 100%)",
      success: "linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)",
      warning: "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
      error: "linear-gradient(135deg, #f44336 0%, #d32f2f 100%)",
    },
    typography: {
      fontFamily: "'Open Sans', 'Roboto', 'Helvetica', 'Arial', sans-serif",
      fontSize: 14,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: {
        fontSize: "3rem",
        fontWeight: 700,
        lineHeight: 1.2,
        letterSpacing: "-0.02em",
      },
      h2: {
        fontSize: "2.25rem",
        fontWeight: 700,
        lineHeight: 1.3,
        letterSpacing: "-0.01em",
      },
      h3: {
        fontSize: "1.875rem",
        fontWeight: 600,
        lineHeight: 1.35,
        letterSpacing: "-0.005em",
      },
      h4: {
        fontSize: "1.5rem",
        fontWeight: 600,
        lineHeight: 1.4,
        letterSpacing: "0em",
      },
      h5: {
        fontSize: "1.25rem",
        fontWeight: 600,
        lineHeight: 1.45,
        letterSpacing: "0em",
      },
      h6: {
        fontSize: "1.125rem",
        fontWeight: 600,
        lineHeight: 1.5,
        letterSpacing: "0em",
      },
      subtitle1: {
        fontSize: "1rem",
        fontWeight: 500,
        lineHeight: 1.75,
        letterSpacing: "0.00938em",
      },
      subtitle2: {
        fontSize: "0.875rem",
        fontWeight: 500,
        lineHeight: 1.57,
        letterSpacing: "0.00714em",
      },
      body1: {
        fontSize: "1rem",
        fontWeight: 400,
        lineHeight: 1.6,
        letterSpacing: "0.00938em",
      },
      body2: {
        fontSize: "0.875rem",
        fontWeight: 400,
        lineHeight: 1.5,
        letterSpacing: "0.01071em",
      },
      button: {
        fontSize: "0.875rem",
        fontWeight: 600,
        lineHeight: 1.75,
        letterSpacing: "0.02857em",
        textTransform: "none",
      },
      caption: {
        fontSize: "0.75rem",
        fontWeight: 400,
        lineHeight: 1.66,
        letterSpacing: "0.03333em",
      },
      overline: {
        fontSize: "0.75rem",
        fontWeight: 600,
        lineHeight: 2.66,
        letterSpacing: "0.08333em",
        textTransform: "uppercase",
      },
    },
    shadows: [
      "none",
      "0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)",
      "0px 3px 6px rgba(0, 0, 0, 0.16), 0px 3px 6px rgba(0, 0, 0, 0.23)",
      "0px 10px 20px rgba(0, 0, 0, 0.19), 0px 6px 6px rgba(0, 0, 0, 0.23)",
      "0px 14px 28px rgba(0, 0, 0, 0.25), 0px 10px 10px rgba(0, 0, 0, 0.22)",
      "0px 19px 38px rgba(0, 0, 0, 0.30), 0px 15px 12px rgba(0, 0, 0, 0.22)",
      "0px 24px 48px rgba(0, 0, 0, 0.35), 0px 19px 19px rgba(0, 0, 0, 0.22)",
      "0px 4px 5px -2px rgba(0,0,0,0.08),0px 7px 10px 1px rgba(0,0,0,0.07),0px 2px 16px 1px rgba(0,0,0,0.06)",
      "0px 5px 5px -3px rgba(0,0,0,0.08),0px 8px 10px 1px rgba(0,0,0,0.07),0px 3px 14px 2px rgba(0,0,0,0.06)",
      "0px 5px 6px -3px rgba(0,0,0,0.08),0px 9px 12px 1px rgba(0,0,0,0.07),0px 3px 16px 2px rgba(0,0,0,0.06)",
      "0px 6px 6px -3px rgba(0,0,0,0.08),0px 10px 14px 1px rgba(0,0,0,0.07),0px 4px 18px 3px rgba(0,0,0,0.06)",
      "0px 6px 7px -4px rgba(0,0,0,0.08),0px 11px 15px 1px rgba(0,0,0,0.07),0px 4px 20px 3px rgba(0,0,0,0.06)",
      "0px 7px 8px -4px rgba(0,0,0,0.08),0px 12px 17px 2px rgba(0,0,0,0.07),0px 5px 22px 4px rgba(0,0,0,0.06)",
      "0px 7px 8px -4px rgba(0,0,0,0.08),0px 13px 19px 2px rgba(0,0,0,0.07),0px 5px 24px 4px rgba(0,0,0,0.06)",
      "0px 7px 9px -4px rgba(0,0,0,0.08),0px 14px 21px 2px rgba(0,0,0,0.07),0px 5px 26px 4px rgba(0,0,0,0.06)",
      "0px 8px 9px -5px rgba(0,0,0,0.08),0px 15px 22px 2px rgba(0,0,0,0.07),0px 6px 28px 5px rgba(0,0,0,0.06)",
      "0px 8px 10px -5px rgba(0,0,0,0.08),0px 16px 24px 2px rgba(0,0,0,0.07),0px 6px 30px 5px rgba(0,0,0,0.06)",
      "0px 8px 11px -5px rgba(0,0,0,0.08),0px 17px 26px 2px rgba(0,0,0,0.07),0px 6px 32px 5px rgba(0,0,0,0.06)",
      "0px 9px 11px -5px rgba(0,0,0,0.08),0px 18px 28px 2px rgba(0,0,0,0.07),0px 7px 34px 6px rgba(0,0,0,0.06)",
      "0px 9px 12px -6px rgba(0,0,0,0.08),0px 19px 29px 2px rgba(0,0,0,0.07),0px 7px 36px 6px rgba(0,0,0,0.06)",
      "0px 10px 13px -6px rgba(0,0,0,0.08),0px 20px 31px 3px rgba(0,0,0,0.07),0px 8px 38px 7px rgba(0,0,0,0.06)",
      "0px 10px 13px -6px rgba(0,0,0,0.08),0px 21px 33px 3px rgba(0,0,0,0.07),0px 8px 40px 7px rgba(0,0,0,0.06)",
      "0px 10px 14px -6px rgba(0,0,0,0.08),0px 22px 35px 3px rgba(0,0,0,0.07),0px 8px 42px 7px rgba(0,0,0,0.06)",
      "0px 11px 14px -7px rgba(0,0,0,0.08),0px 23px 36px 3px rgba(0,0,0,0.07),0px 9px 44px 8px rgba(0,0,0,0.06)",
      "0px 11px 15px -7px rgba(0,0,0,0.08),0px 24px 38px 3px rgba(0,0,0,0.07),0px 9px 46px 8px rgba(0,0,0,0.06)",
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: mode === "light" ? "#ffffff" : "#121212",
            minHeight: "100vh",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            background: mode === "light" 
              ? "rgba(255, 255, 255, 0.95)" 
              : "rgba(30, 30, 30, 0.95)",
            backdropFilter: "blur(20px)",
            borderBottom: mode === "light" 
              ? "1px solid rgba(0, 0, 0, 0.08)" 
              : "1px solid rgba(255, 255, 255, 0.08)",
            boxShadow: mode === "light" 
              ? "0px 1px 3px rgba(0, 0, 0, 0.1)" 
              : "0px 1px 3px rgba(0, 0, 0, 0.3)",
            color: mode === "light" ? "rgba(0, 0, 0, 0.87)" : "rgba(255, 255, 255, 0.87)",
            borderRadius: 0,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: 0,
            backgroundColor: mode === "light" ? "#ffffff" : "#1e1e1e",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            borderRadius: 8,
            fontWeight: 600,
            fontSize: "0.875rem",
            padding: "10px 24px",
            boxShadow: "none",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              boxShadow: mode === "light" 
                ? "0px 8px 25px rgba(0, 0, 0, 0.15)" 
                : "0px 8px 25px rgba(0, 0, 0, 0.4)",
              transform: "translateY(-2px)",
            },
            "&:active": {
              transform: "translateY(0px)",
            },
            "&.Mui-disabled": {
              backgroundColor: mode === "light" ? "#f5f5f5" : "#424242",
              color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.26)",
              boxShadow: "none",
              transform: "none",
              cursor: "not-allowed",
            },
          },
          contained: {
            background: mode === "light" 
              ? "linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)"
              : "linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)",
            color: mode === "light" ? "#ffffff" : "#1a1a1a",
            "&:hover": {
              background: mode === "light"
                ? "linear-gradient(135deg, #333333 0%, #1a1a1a 100%)"
                : "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
              boxShadow: mode === "light"
                ? "0px 8px 25px rgba(26, 26, 26, 0.3)"
                : "0px 8px 25px rgba(255, 255, 255, 0.3)",
            },
            "&.Mui-disabled": {
              background: mode === "light" ? "#e0e0e0" : "#424242",
              color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.26)",
              boxShadow: "none",
              transform: "none",
            },
          },
          outlined: {
            borderWidth: 2,
            borderColor: mode === "light" ? "rgba(26, 26, 26, 0.2)" : "rgba(255, 255, 255, 0.2)",
            color: mode === "light" ? "#1a1a1a" : "#ffffff",
            "&:hover": {
              borderWidth: 2,
              borderColor: mode === "light" ? "#1a1a1a" : "#ffffff",
              backgroundColor: mode === "light" 
                ? "rgba(248, 250, 254, 0.5)" 
                : "rgba(255, 255, 255, 0.1)",
            },
            "&.Mui-disabled": {
              borderColor: mode === "light" ? "rgba(0, 0, 0, 0.12)" : "rgba(255, 255, 255, 0.12)",
              color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.26)",
              backgroundColor: "transparent",
              transform: "none",
            },
          },
          text: {
            color: mode === "light" ? "#1a1a1a" : "#ffffff",
            "&:hover": {
              backgroundColor: mode === "light" 
                ? "rgba(248, 250, 254, 0.5)" 
                : "rgba(255, 255, 255, 0.1)",
            },
            "&.Mui-disabled": {
              color: mode === "light" ? "rgba(0, 0, 0, 0.26)" : "rgba(255, 255, 255, 0.26)",
              backgroundColor: "transparent",
              transform: "none",
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            boxShadow: mode === "light" 
              ? "0px 10px 40px rgba(0, 0, 0, 0.1)" 
              : "0px 10px 40px rgba(0, 0, 0, 0.3)",
            border: mode === "light" 
              ? "1px solid rgba(255, 255, 255, 0.2)" 
              : "1px solid rgba(255, 255, 255, 0.1)",
            backdropFilter: "blur(20px)",
            background: mode === "light" 
              ? "rgba(255, 255, 255, 0.9)" 
              : "rgba(30, 30, 30, 0.9)",
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            borderRadius: 12,
            border: mode === "light" 
              ? "1px solid rgba(255, 255, 255, 0.2)" 
              : "1px solid rgba(255, 255, 255, 0.1)",
          },
          elevation1: {
            boxShadow: mode === "light" 
              ? "0px 10px 40px rgba(0, 0, 0, 0.1)" 
              : "0px 10px 40px rgba(0, 0, 0, 0.3)",
          },
          elevation2: {
            boxShadow: mode === "light" 
              ? "0px 20px 60px rgba(0, 0, 0, 0.15)" 
              : "0px 20px 60px rgba(0, 0, 0, 0.4)",
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 8,
              backgroundColor: mode === "light" 
                ? "rgba(255, 255, 255, 0.9)" 
                : "rgba(45, 45, 45, 0.9)",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
              "& fieldset": {
                borderColor: mode === "light" 
                  ? "rgba(0, 0, 0, 0.1)" 
                  : "rgba(255, 255, 255, 0.1)",
                borderWidth: 1,
              },
              "&:hover fieldset": {
                borderColor: mode === "light" 
                  ? "rgba(26, 26, 26, 0.3)" 
                  : "rgba(255, 255, 255, 0.3)",
              },
              "&.Mui-focused fieldset": {
                borderColor: mode === "light" ? "#1a1a1a" : "#ffffff",
                borderWidth: 2,
              },
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              backgroundColor: mode === "light" 
                ? "rgba(248, 250, 254, 0.8)" 
                : "rgba(255, 255, 255, 0.1)",
              transform: "scale(1.05)",
            },
            '&[aria-label="open drawer"], &[aria-label="close drawer"]': {
              color: mode === "light" ? "rgba(0, 0, 0, 0.87)" : "rgba(255, 255, 255, 0.87)",
            },
          },
        },
      },
      // MuiListItemButton: {
      //   styleOverrides: {
      //     root: {
      //       borderRadius: 8,
      //       margin: "4px 8px",
      //       transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      //       "&.Mui-selected": {
      //         background: mode === "light"
      //           ? "linear-gradient(135deg, rgba(248, 250, 254, 0.8) 0%, rgba(255, 255, 255, 0.95) 100%)"
      //           : "linear-gradient(135deg, rgba(45, 45, 45, 0.8) 0%, rgba(60, 60, 60, 0.95) 100%)",
      //         border: mode === "light" 
      //           ? "1px solid rgba(26, 26, 26, 0.15)" 
      //           : "1px solid rgba(255, 255, 255, 0.15)",
      //         borderRadius: 8,
      //         color: mode === "light" ? "#1a1a1a" : "#ffffff",
      //         "&:hover": {
      //           background: mode === "light"
      //             ? "linear-gradient(135deg, rgba(248, 250, 254, 0.9) 0%, rgba(255, 255, 255, 1) 100%)"
      //             : "linear-gradient(135deg, rgba(45, 45, 45, 0.9) 0%, rgba(60, 60, 60, 1) 100%)",
      //         },
      //       },
      //       "&:hover": {
      //         backgroundColor: mode === "light" 
      //           ? "rgba(248, 250, 254, 0.5)" 
      //           : "rgba(255, 255, 255, 0.1)",
      //       },
      //     },
      //   },
      // },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            fontWeight: 500,
          },
          filled: {
            backgroundColor: mode === "light" 
              ? "rgba(248, 250, 254, 0.8)" 
              : "rgba(45, 45, 45, 0.8)",
            color: mode === "light" ? "#1a1a1a" : "#ffffff",
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            boxShadow: mode === "light" 
              ? "0px 4px 14px rgba(0, 0, 0, 0.1)" 
              : "0px 4px 14px rgba(0, 0, 0, 0.3)",
          },
        },
      },
    },
    customComponents: {
      card: {
        white: {
          background: mode === "light" 
            ? "rgba(255, 255, 255, 0.95)" 
            : "rgba(30, 30, 30, 0.95)",
          backdropFilter: "blur(20px)",
          borderRadius: 2,
          border: mode === "light" 
            ? "1px solid rgba(255, 255, 255, 0.2)" 
            : "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: mode === "light" 
            ? "0px 10px 40px rgba(0, 0, 0, 0.1)" 
            : "0px 10px 40px rgba(0, 0, 0, 0.3)",
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
        dark: {
          background: "linear-gradient(135deg, rgba(45, 62, 80, 0.95) 0%, rgba(52, 73, 94, 0.95) 100%)",
          backdropFilter: "blur(20px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0px 20px 60px rgba(0, 0, 0, 0.3)",
          borderRadius: 2,
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          "& .MuiTypography-root": {
            color: "white",
          },
          "& .MuiTypography-secondary": {
            color: "rgba(255, 255, 255, 0.7)",
          }
        },
        static: {
          background: mode === "light" 
            ? "rgba(255, 255, 255, 0.95)" 
            : "rgba(30, 30, 30, 0.95)",
          backdropFilter: "blur(20px)",
          borderRadius: 2,
          border: mode === "light" 
            ? "1px solid rgba(255, 255, 255, 0.2)" 
            : "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: mode === "light" 
            ? "0px 10px 40px rgba(0, 0, 0, 0.1)" 
            : "0px 10px 40px rgba(0, 0, 0, 0.3)",
          p: 3,
          height: "100%",
        },
        referral: {
          background: mode === "light"
            ? "linear-gradient(135deg, rgba(248, 250, 254, 0.8) 0%, rgba(255, 255, 255, 0.95) 100%)"
            : "linear-gradient(135deg, rgba(45, 45, 45, 0.8) 0%, rgba(30, 30, 30, 0.95) 100%)",
          backdropFilter: "blur(20px)",
          borderRadius: 2,
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          boxShadow: mode === "light" 
            ? "0 20px 60px 0 rgba(248, 250, 254, 0.2)" 
            : "0 20px 60px 0 rgba(0, 0, 0, 0.4)",
          border: mode === "light" 
            ? "1px solid rgba(248, 250, 254, 0.2)" 
            : "1px solid rgba(255, 255, 255, 0.1)",
          "&:before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: mode === "light"
              ? "linear-gradient(135deg, rgba(248, 250, 254, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)"
              : "linear-gradient(135deg, rgba(45, 45, 45, 0.15) 0%, rgba(30, 30, 30, 0.05) 100%)",
            zIndex: 0,
          },
          "& > *": {
            position: "relative",
            zIndex: 1,
          },
        },
        darkSection: {
          background: "linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(18, 18, 18, 0.95) 100%)",
          backdropFilter: "blur(20px)",
          borderRadius: 2,
          p: 3,
          height: "100%",
          border: "1px solid rgba(255, 255, 255, 0.05)",
          boxShadow: "0px 20px 60px rgba(0, 0, 0, 0.4)",
          "& .MuiTypography-root": {
            color: "white",
          },
          "& .MuiTypography-secondary": {
            color: "rgba(255, 255, 255, 0.7)",
          },
          "& .section-content": {
            background: mode === "light" 
              ? "rgba(255, 255, 255, 0.95)" 
              : "rgba(30, 30, 30, 0.95)",
            backdropFilter: "blur(20px)",
            borderRadius: 2,
            p: 3,
            boxShadow: "0px 10px 40px rgba(0, 0, 0, 0.2)",
            border: mode === "light" 
              ? "1px solid rgba(255, 255, 255, 0.2)" 
              : "1px solid rgba(255, 255, 255, 0.1)",
          }
        },
        glass: {
          background: mode === "light" 
            ? "rgba(255, 255, 255, 0.1)" 
            : "rgba(30, 30, 30, 0.1)",
          backdropFilter: "blur(20px)",
          borderRadius: 2,
          border: mode === "light" 
            ? "1px solid rgba(255, 255, 255, 0.2)" 
            : "1px solid rgba(255, 255, 255, 0.1)",
          boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.37)",
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
        elevated: {
          background: mode === "light" 
            ? "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)" 
            : "linear-gradient(135deg, #2d2d2d 0%, #1a1a1a 100%)",
          borderRadius: 2,
          border: mode === "light" 
            ? "1px solid rgba(0, 0, 0, 0.05)" 
            : "1px solid rgba(255, 255, 255, 0.05)",
          boxShadow: mode === "light" 
            ? "0px 20px 60px rgba(0, 0, 0, 0.1)" 
            : "0px 20px 60px rgba(0, 0, 0, 0.4)",
          p: 3,
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      },
      container: {
        main: {
          background: "transparent",
          p: { xs: 2, md: 4 },
        },
        dark: {
          background: "rgba(18, 18, 18, 0.8)",
          backdropFilter: "blur(20px)",
          p: { xs: 2, md: 4 },
          borderRadius: 2,
          border: "1px solid rgba(255, 255, 255, 0.05)",
        },
        gradient: {
          background: mode === "light"
            ? "linear-gradient(135deg, rgba(248, 250, 254, 0.8) 0%, rgba(255, 255, 255, 0.95) 100%)"
            : "linear-gradient(135deg, rgba(45, 45, 45, 0.8) 0%, rgba(30, 30, 30, 0.95) 100%)",
          backdropFilter: "blur(20px)",
          p: { xs: 2, md: 4 },
          borderRadius: 2,
          border: mode === "light" 
            ? "1px solid rgba(255, 255, 255, 0.2)" 
            : "1px solid rgba(255, 255, 255, 0.1)",
        }
      }
    }
  })
);

// Default light theme for backwards compatibility
export const theme = createAppTheme("light");

export const authTextFieldStyle = {
  borderRadius: 8,
  "& .MuiOutlinedInput-root": {
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    backdropFilter: "blur(20px)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    "& fieldset": {
      borderColor: "rgba(0, 0, 0, 0.1)",
      borderWidth: 1,
    },
    "&:hover fieldset": {
      borderColor: "rgba(26, 26, 26, 0.3)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1a1a1a",
      borderWidth: 2,
      boxShadow: "0 0 0 3px rgba(248, 250, 254, 0.5)",
    },
  },
  "& .MuiInputLabel-root": {
    color: "rgba(0, 0, 0, 0.6)",
    fontWeight: 500,
    "&.Mui-focused": {
      color: "#1a1a1a",
    },
  },
  "& .MuiInputBase-input": {
    color: "rgba(0, 0, 0, 0.87)",
    fontWeight: 400,
  },
};
